import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useUser } from "../contexts/useUser";
import { API, Storage } from "aws-amplify";
import { useQueryClient } from "@tanstack/react-query";

const FileUploader = ({
  agentId,
  projectId,
}: {
  agentId: string;
  projectId: string;
}) => {
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const { user } = useUser();

  const [ragSplitter, setRagSplitter] = useState("default");
  const [ragChunkSize, setRagChuckSize] = useState("1000");
  const [ragChunkSizeError, setRagChunkSizeError] = useState("");
  const [ragChunkOverlap, setRagChuckOverlap] = useState("10");
  const [ragChunkOverlapError, setRagChunkOverlapError] = useState("");
  const [fileTooBigError, setFileTooBigError] = useState(""); 

  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();

  const handleFileUpload = (e: any) => {
    const file = e.target.files?.[0];

    if (file.size > 5000000) {
      setFileTooBigError("File too large. Max size is 5MB. Email support for an exception (help@llmasaservice.io)");
      return;
    }
    else {
      setFileTooBigError("");
    }

    if (file) {
      setSelectedFile(file);
    }
  };

  const onSubmit = async () => {
    if (!selectedFile) {
      console.error("No file selected");
      return;
    }

    if (
      ragChunkSize.length === 0 ||
      isNaN(parseInt(ragChunkSize)) ||
      parseInt(ragChunkSize) < 1 ||
      parseInt(ragChunkSize) > 10000
    ) {
      setRagChunkSizeError("Please enter a number 1 to 10000 characters");
      return;
    } else {
      setRagChunkSizeError("");
    }

    if (
      ragChunkOverlap.length === 0 ||
      isNaN(parseInt(ragChunkOverlap)) ||
      parseInt(ragChunkOverlap) < 0 ||
      parseInt(ragChunkOverlap) >= parseInt(ragChunkSize)
    ) {
      setRagChunkOverlapError(
        "Please enter a number 0 to " +
          (parseInt(ragChunkSize) - 1) +
          " characters"
      );
      return;
    } else {
      setRagChunkOverlapError("");
    }

    try {
      setUploading(true);
      setUploadMessage("Uploading file to storage...");

      console.log("Uploading file", selectedFile);

      //upload the file to S3 and get the location
      const uploadResult = await Storage.put(
        agentId + "/" + selectedFile.name,
        selectedFile,
        {
          level: "public",
          contentType: selectedFile.type,
        }
      );

      console.log("Upload result", uploadResult);
      const location = uploadResult.key;

      setUploadMessage("Processing and indexing document...");
      const r = await API.post("eventapi", "/uploadDocumentToAgent", {
        body: {
          projectId: projectId,
          agentId: agentId,
          location: location,
          fileName: selectedFile.name,
          fileSize: selectedFile.size.toString(),
          fileType: selectedFile.type,
          uploadDate: new Date().toISOString(),
          uploader: user?.displayName || "",
          lastModifiedDate:
            selectedFile.lastModifiedDate?.toISOString() ||
            new Date(selectedFile.lastModified).toISOString(),
          type: "file",
          ragSplitter: ragSplitter,
          ragChunkSize: ragChunkSize,
          ragChunkOverlap: ragChunkOverlap,
        },
      });

      console.log("Upload response", r);

      queryClient.invalidateQueries({
        queryKey: ["LLMAgentDocuments", { agentId: agentId }],
      });

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error uploading file", error);
    } finally {
      setUploading(false);
      setUploadMessage("");
    }
  };

  return (
    <Box>
      <Dialog open={uploading} maxWidth="xs" fullWidth>
        <DialogTitle>Uploading Document</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} alignItems="center" mb={2}>
            <CircularProgress size={24} />
            <Typography variant="body2">
              {uploadMessage || "Working..."}
            </Typography>
          </Box>
          <LinearProgress />
          <Typography variant="caption" display="block" mt={2}>
            This can take a few minutes while the document is chunked and indexed.
          </Typography>
        </DialogContent>
      </Dialog>
      <Typography variant="body2" mb={1}>
        Upload new or replace existing file...
      </Typography>
      <input
        type="file"
        accept=".pdf,.txt,.cpp,.go,.java,.js,.php,.proto,.python,.py,.rst,.ruby,.rust,.scala,.swift,.markdown,.md,.latex,.html,.sol,.ts,.tsx,.jsx,.c,.csv"
        onChange={handleFileUpload}
        ref={fileInputRef}
        disabled={uploading}
      />
      {fileTooBigError && (
        <Typography color="error">{fileTooBigError}</Typography>
      )}

      {selectedFile && (
        <Grid container spacing={2} mt={2}>
          <Grid item xs={3}>
            <TextField
              defaultValue={selectedFile.name}
              name="fileName"
              label="File Name"
              size="small"
              fullWidth
              disabled
              variant="standard"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              defaultValue={selectedFile.size}
              label="File Size (bytes)"
              size="small"
              fullWidth
              disabled
              variant="standard"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              defaultValue={selectedFile.type}
              name="fileType"
              label="File Type"
              size="small"
              fullWidth
              disabled
              variant="standard"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              defaultValue={selectedFile.lastModifiedDate?.toISOString()}
              name="lastModifiedDate"
              label="Last Modified Date"
              size="small"
              fullWidth
              disabled
              variant="standard"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="rag-splitter-label">Document splitter</InputLabel>
              <Select
                labelId="rag-splitter-label"
              value={ragSplitter}
              name="ragSplitter"
              label="Document splitter"
              onChange={(e) => setRagSplitter(e.target.value)}
              disabled={uploading}
            >
                <MenuItem value="default">
                  Default (recursive text splitter)
                </MenuItem>
                <MenuItem value="newline">New-line character (/n good for CSV)</MenuItem>
                <MenuItem value="blankline">Blank line (/n/n)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <TextField
            type="number"
              defaultValue={ragChunkSize}
              name="ragChuckSize"
              label={ragSplitter === "default" ? "Chunk Size (characters)" : "Maximum Chunk Size Before Sub-Splitting (characters)"}
              size="small"
              fullWidth
              onChange={(e) => {
                setRagChuckSize(e.target.value)
              }}
              helperText={ragChunkSizeError}
              error={!!ragChunkSizeError}
              disabled={uploading}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
            type="number"
              defaultValue={ragChunkOverlap}
              name="ragChuckOverlap"
              label="Chunk Overlap Size (bytes)"
              size="small"
              fullWidth
              onChange={(e) => setRagChuckOverlap(e.target.value)}
              helperText={ragChunkOverlapError}
              error={!!ragChunkOverlapError}
              disabled={uploading}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={() => onSubmit()}
              disabled={uploading}
            >
              Upload
            </Button>
            &nbsp;&nbsp;
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default FileUploader;
